
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ScrollText } from 'lucide-react';
import { ScriptureReferenceType } from './types';

interface ScriptureReferencesProps {
  references: ScriptureReferenceType[];
  contextualTitle?: string;
}

export const ScriptureReferences: React.FC<ScriptureReferencesProps> = ({ 
  references,
  contextualTitle 
}) => {
  // Validate the references array
  if (!references || !Array.isArray(references) || references.length === 0) {
    console.log("No scripture references to display:", references);
    return null;
  }
  
  // Filter out any invalid references
  const validReferences = references.filter(
    ref => ref && typeof ref === 'object' && ref.reference && ref.text
  );
  
  if (validReferences.length === 0) {
    console.log("No valid scripture references found:", references);
    return null;
  }
  
  // Clean and process references for display
  const processedReferences = validReferences.map(ref => {
    // Clean up reference format
    const reference = typeof ref.reference === 'string' 
      ? ref.reference.replace(/\s+/g, ' ').trim()
      : 'Scripture reference';
    
    // Clean up text format - make sure we get the complete verse text
    let text = typeof ref.text === 'string'
      ? ref.text
          .replace(/\*\*/g, '')
          .replace(/^\s*["']|["']\s*$/g, '')
          .replace(/\\"/g, '"')
          .replace(/\\'/g, "'")
          .trim()
      : 'Scripture text';
      
    // Ensure we don't have partial quotes with just opening or closing quotes
    if (text.startsWith('"') && !text.endsWith('"') && !text.includes('"', 1)) {
      text = text + '"';
    }
    
    // Make sure text isn't truncated with ellipses
    if (text.endsWith('...') && text.length < 100) {
      text = text.replace(/\.\.\.$/, '');
    }
    
    // If text is just numbers, mark it as a verse reference
    if (/^\d+\s+\d+$/.test(text)) {
      text = `Verse ${text}`;
    }
    
    // Clean up explanation format if present
    const explanation = typeof ref.explanation === 'string'
      ? ref.explanation.trim()
      : undefined;
      
    return { reference, text, explanation };
  });
  
  console.log("Displaying scripture references:", processedReferences);
  
  return (
    <Card className="border-heaven-gold/10">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-heaven-gold text-lg">
          <ScrollText className="h-5 w-5" />
          <span>{contextualTitle || "Scripture References"}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {processedReferences.map((scripture, i) => (
          <div key={i} className="p-3 bg-heaven-gold/5 rounded text-left">
            <p className="font-semibold text-heaven-gold mb-1">{scripture.reference}</p>
            <p className="italic text-gray-700 mb-2">"{scripture.text}"</p>
            {scripture.explanation && (
              <p className="text-sm text-gray-600 mt-1">{scripture.explanation}</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
